// UI driver entry point.

import { installHoverTooltipDriver } from './hoverTooltip';
import { installPanelsDriver } from './panels';
import { installPaneRefreshDriver } from './paneSync';
import { installChromeDriver } from './chrome';
import { initTopbar } from './topbar';
import { initLayersPanel } from './layersPanel';
import { initConsole } from './console';
import { installWindowRegistry } from './windowManager';
import { installWindowFocus } from './windowManager/focus';
import { installReductionPlots } from './reductions/plotWindow';
import { installSelectionInfo } from './selections/infoWindow';
import { installPaletteDriver } from './palette';
import { installShortcutsHelpTab } from './palette/helpTab';
import { installColorbarStack } from './colorbarStack';
import { installColorbarDock } from './colorbarDock';
import { installCameraGnomon } from './cameraGnomon';
import { installCameraProjectionToggle } from './cameraGnomon/projectionToggle';
import { installNavModeBadge } from './navModeBadge';
import { installFlyBanner } from './flyBanner';
import { installSectionRebuildDriver } from './panel/sections';
import { applyControlPaneStyles } from './panel/theme';
import { applyThemeCssVars } from './themeVars';
import { INTENTS } from '../store/intents';
import { debugWarn } from '@adapters/debug';
import { setupStatsPanel } from './dom';
import type { Store } from '../store/store';
import type { State } from '../store/schema';
import type { ViewerContext } from '../app/context';
import type { KeyboardCtx } from '@input/shortcuts';
import type { UiHandle } from './headless';
import type { Disposer } from './types';

interface InitUIDeps {
    onWindowResize?: () => void;
    installPaneUI?: () => Disposer | void;
    initKeyboardShortcuts?: (ctx: KeyboardCtx) => () => void;
    keyboardCtx?: KeyboardCtx;
    sectionDeps?: Parameters<typeof installSectionRebuildDriver>[1];
}

export function initUI(store: Store<State>, viewer: ViewerContext, deps: InitUIDeps): UiHandle {
    const offs: Array<() => void> = [];

    // Install the WindowRegistry first so any subsequent driver can register
    // floating windows on it (Console first; reductions/probes/palette later).
    const wm = installWindowRegistry();
    offs.push(wm.dispose);
    offs.push(installWindowFocus());
    offs.push(installReductionPlots(wm.registry));
    offs.push(installSelectionInfo(viewer, wm.registry));

    offs.push(installHoverTooltipDriver(store));
    offs.push(installPaneRefreshDriver(store));
    offs.push(installPanelsDriver(store, viewer, { onWindowResize: deps.onWindowResize }));
    offs.push(initTopbar(viewer));
    offs.push(initLayersPanel(viewer, wm.registry));
    offs.push(initConsole(store, viewer, wm.registry));
    offs.push(installColorbarStack(store, viewer));
    // Store the gnomon disposer on the viewer so a camera projection swap can
    // dispose + reinstall it against the new camera; the UI teardown calls it too.
    viewer.dispose.cameraGnomon = installCameraGnomon(viewer);
    offs.push(() => viewer.dispose.cameraGnomon?.());
    // Projection toggle lives outside the gnomon module so it survives the
    // gnomon's dispose/reinstall during a swap.
    offs.push(installCameraProjectionToggle());
    // Navigation-mode badge + fly-mode banner (standalone, survive the gnomon swap).
    offs.push(installNavModeBadge());
    offs.push(installFlyBanner());
    // Auto-dock the colorbar after the bottom cluster + reflow it vertical when
    // the viewport is too narrow. Installed after the cluster chips + panel drag
    // wiring so the cluster rect and the `.dragging` class signal both exist.
    offs.push(installColorbarDock());
    offs.push(installChromeDriver(store));
    // Stage 7 — ⌘K command palette + shortcut registry. Installed before
    // shortcuts are registered through `deps.initKeyboardShortcuts` so the
    // global key listener is up when initKeyboardShortcuts registers commands.
    offs.push(installPaletteDriver({ viewer, windowRegistry: wm.registry }));
    offs.push(installShortcutsHelpTab());
    if (deps.sectionDeps) offs.push(installSectionRebuildDriver(viewer, deps.sectionDeps));
    offs.push(store.onIntent(INTENTS.UI_THEME_APPLY, (p) => {
        const theme = (p && p.theme) || ({} as Record<string, unknown>);
        applyControlPaneStyles(theme);
        applyThemeCssVars(theme);
    }));
    if (viewer.theme && viewer.theme.current) {
        applyControlPaneStyles(viewer.theme.current);
        applyThemeCssVars(viewer.theme.current);
    }

    offs.push(setupStatsPanel(viewer));

    if (typeof deps.installPaneUI === 'function') {
        try {
            const offPane = deps.installPaneUI();
            if (typeof offPane === 'function') offs.push(offPane);
        }
        catch (e) { debugWarn(viewer, 'installPaneUI failed:', e); }
    }

    let disposeKeyboard: (() => void) | null = null;
    if (typeof deps.initKeyboardShortcuts === 'function' && deps.keyboardCtx) {
        try { disposeKeyboard = deps.initKeyboardShortcuts(deps.keyboardCtx); }
        catch (e) { debugWarn(viewer, 'initKeyboardShortcuts failed:', e); }
    }

    // Initial pane state (help-start, mobile-portrait collapse) is settled
    // by bootstrap before the cold-start fade reveals the panes.

    return {
        dispose() {
            offs.forEach((off) => { try { off(); } catch { /* swallow */ } });
            if (typeof disposeKeyboard === 'function') {
                try { disposeKeyboard(); } catch { /* swallow */ }
            }
        },
    };
}
